import {
  ATOMIC_BOMB_JEWEL_ID,
  type JewelSocketProfile,
  MISSILE_JEWEL_ID,
  toExactBigInt,
} from '@kenocyber/shared';
import { ATOMIC_BOMB_MAX_CHANCE_BPS } from './atomic-bomb';
import { MISSILE_MAX_CHANCE_BPS, type MissileSelection, missileDamageMilli } from './missile';
import type { DamageOrigin, JewelEmittedEffect } from './origin';
import { damageOriginMayTrigger } from './origin';
import { PROC_CHANCE_DENOMINATOR, procChanceNumerator } from './proc-lottery';

/**
 * **A loteria por SALVA: uma rolagem, e no máximo um proc ofensivo de disparo.**
 *
 * ## Porque este ficheiro nasce agora e não nasceu com a Bomba
 *
 * Porque com um único ocupante não havia intervalo nenhum a repartir. A Bomba
 * sorteia uma vez por salva e compara a rolagem contra o seu numerador; isso É
 * o intervalo `[0, w)`, escrito da forma mais curta que existe, e uma tabela com
 * uma linha não teria acrescentado nada.
 *
 * Com o segundo ocupante a pergunta muda: onde começa o intervalo do míssil? A
 * resposta tem de ser a largura da Bomba, e tem de ser a MESMA largura que a
 * Bomba usa - senão os dois espaços sobrepõem-se e uma salva pode produzir os
 * dois, ou afastam-se e as chances individuais deixam de somar o que a etiqueta
 * promete.
 *
 * ## A ordem é o contrato
 *
 * {@link VOLLEY_PROC_REGISTRY} está por ordem de CHEGADA, não alfabética, e é
 * deliberado: a Bomba é a primeira porque já foi implantada com o intervalo
 * `[0, w)`, e mover o início dela mudaria o resultado de toda a rolagem que já
 * acontece hoje - com a mesma semente, a mesma salva e a mesma joia. Uma ordem
 * derivada do nome seria uma ordem que a terceira joia pode reorganizar sem que
 * ninguém repare.
 *
 * `volley-lottery.test.ts` afirma-o diretamente: o intervalo que este registo dá
 * à Bomba é exatamente `[0, atomicBombChanceNumerator(nível))`, que é o que
 * `selectVolleyBomb` compara - e é por isso que **`atomic-bomb.ts` não muda uma
 * linha neste gate**.
 *
 * ## O que "no máximo um" quer dizer, e como é garantido
 *
 * Por CONSTRUÇÃO, e não por uma verificação. Os intervalos são disjuntos e
 * consecutivos sobre uma única rolagem, portanto uma salva que caia no da Bomba
 * não pode cair no do míssil - não há dois sorteios independentes, e nenhum
 * chamador tem de se lembrar de perguntar ao outro.
 *
 * ## E o espaço chega
 *
 * `6 % + 10 % = 16 %` do espaço no pior conjunto legal, com 84 % a sobrar para a
 * salva comum. As joias futuras que sorteiem por salva entram por aqui, no fim,
 * e a soma continua a ser verificada: acima do denominador isto RECUSA, como a
 * loteria por projétil recusa, em vez de normalizar um conjunto de tetos que
 * ninguém equilibrou.
 */

/**
 * Os procs que uma joia pode originar uma vez por SALVA.
 *
 * Um subconjunto próprio de {@link JewelEmittedEffect}, como
 * `OutgoingJewelProcId` é: a diferença entre as duas listas é a UNIDADE do
 * sorteio, que é a única coisa que separa as duas loterias.
 */
export type VolleyProcId = Extract<JewelEmittedEffect, 'atomic_blast' | 'missile'>;

export interface VolleyProcDefinition {
  readonly procId: VolleyProcId;
  readonly jewelId: string;
  readonly maxChanceBps: number;
}

/**
 * **O registo canónico, por ordem de chegada.** Mover uma linha muda o jogo.
 *
 * Os tetos não são escritos aqui: são importados de onde a joia os declara, para
 * que a tabela do contrato de cada uma continue a ter um só dono.
 */
export const VOLLEY_PROC_REGISTRY: readonly VolleyProcDefinition[] = Object.freeze([
  Object.freeze({
    procId: 'atomic_blast' as const,
    jewelId: ATOMIC_BOMB_JEWEL_ID,
    maxChanceBps: ATOMIC_BOMB_MAX_CHANCE_BPS,
  }),
  Object.freeze({
    procId: 'missile' as const,
    jewelId: MISSILE_JEWEL_ID,
    maxChanceBps: MISSILE_MAX_CHANCE_BPS,
  }),
]);

/** Um intervalo desta nave, em unidades do espaço de rolagem por salva. */
export interface VolleyInterval {
  readonly procId: VolleyProcId;
  readonly from: number;
  readonly to: number;
  /** A soma de níveis que produziu esta largura. A magnitude sai da MESMA passagem. */
  readonly levelSum: bigint;
}

/**
 * Os intervalos desta nave, por ordem canónica de chegada.
 *
 * Uma joia ausente do perfil não ocupa espaço e não desloca as seguintes - o
 * cursor só avança pelo que existe, portanto um casco com o míssil e sem a Bomba
 * tem o míssil em `[0, w)` e a sua chance é exatamente a que a etiqueta diz.
 *
 * Uma joia presente com nível zero também não ocupa: um intervalo de largura
 * zero é um intervalo em que nenhuma rolagem cai, e criá-lo só serviria para
 * deslocar o seguinte por nada.
 */
export function volleyProcIntervals(profile: JewelSocketProfile): readonly VolleyInterval[] {
  const intervals: VolleyInterval[] = [];
  let cursor = 0;
  for (const definition of VOLLEY_PROC_REGISTRY) {
    const owned = profile.entries.find((entry) => entry.id === definition.jewelId);
    if (!owned) {
      continue;
    }
    const levelSum = toExactBigInt(owned.levelSum, `${owned.id} levelSum`);
    const width = procChanceNumerator(definition.maxChanceBps, Number(levelSum));
    if (width <= 0) {
      continue;
    }
    intervals.push({ procId: definition.procId, from: cursor, to: cursor + width, levelSum });
    cursor += width;
  }
  if (cursor > PROC_CHANCE_DENOMINATOR) {
    /*
     * Recusa em vez de normalizar, exatamente como a loteria por projétil.
     * Normalizar esconderia um conjunto de tetos que somam mais de cem por
     * cento, que é uma decisão de equilíbrio por fazer.
     */
    throw new RangeError(
      `volley proc intervals exceed the roll space: ${cursor} > ${PROC_CHANCE_DENOMINATOR}`,
    );
  }
  return intervals;
}

/** O intervalo de um proc por salva neste perfil, ou `null` se ele não o tem. */
export function volleyProcInterval(
  profile: JewelSocketProfile,
  procId: VolleyProcId,
): VolleyInterval | null {
  return volleyProcIntervals(profile).find((interval) => interval.procId === procId) ?? null;
}

/**
 * **A seleção do míssil.** Uma rolagem por salva, e o intervalo é o do registo.
 *
 * `roll` tem de vir de `[0, PROC_CHANCE_DENOMINATOR)` e ser **o mesmo valor** que
 * a Bomba comparou nesta salva - é isso que faz os dois sorteios serem um.
 *
 * Devolve `null` quando a rolagem cai fora do intervalo, quando a nave não tem a
 * joia, e quando a origem não pode originar joia nenhuma. As três razões são
 * diferentes e nenhuma produz míssil.
 */
export function selectVolleyMissile(
  profile: JewelSocketProfile,
  origin: DamageOrigin,
  roll: number,
  entropy: number,
  baseMilli: bigint,
): MissileSelection | null {
  /*
   * **A tabela fechada, nas suas DUAS metades** (gate V28G.P).
   *
   * É aqui que a exclusão do contrato V28G.P deixa de depender de não existir
   * chamador: uma serra NÃO pode lançar um míssil nem largar um Núcleo Atômico,
   * e a razão é estrutural - as duas são sorteadas uma vez por SALVA, e uma
   * serra não dispara salva nenhuma. A linha abaixo diz o mesmo uma segunda
   * vez, para que escrever o chamador que nao existe continue a nao produzir
   * nada.
   */
  if (!damageOriginMayTrigger(origin, 'missile')) {
    return null;
  }
  if (!Number.isInteger(roll) || roll < 0 || roll >= PROC_CHANCE_DENOMINATOR) {
    throw new RangeError(`volley missile roll outside [0, ${PROC_CHANCE_DENOMINATOR}): ${roll}`);
  }
  if (typeof baseMilli !== 'bigint' || baseMilli < 0n) {
    throw new RangeError(`a missile base is a non-negative bigint, received ${String(baseMilli)}`);
  }
  const interval = volleyProcInterval(profile, 'missile');
  if (interval === null || roll < interval.from || roll >= interval.to) {
    return null;
  }
  return Object.freeze({
    entropy: entropy >>> 0,
    damageMilli: missileDamageMilli(baseMilli, interval.levelSum),
  });
}

/**
 * O intervalo que a Bomba ocupa neste perfil, **como o registo o vê**.
 *
 * Exportado para a prova poder comparar as duas verdades em vez de as repetir:
 * este número tem de ser exatamente `[0, atomicBombChanceNumerator(nível))`, que
 * é o que `selectVolleyBomb` compara sozinho. Se algum dia deixar de ser, é
 * porque alguém mexeu na ordem do registo - e é aí que a prova falha, em vez de
 * a Bomba mudar de chance em silêncio.
 */
export function atomicBombVolleyInterval(profile: JewelSocketProfile): VolleyInterval | null {
  return volleyProcInterval(profile, 'atomic_blast');
}
